import React, { useEffect, useRef, useState } from "react";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Form, Formik, useFormikContext } from "formik";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import { CircularIndeterminate, FormButton, SpinnerOverlay } from "components";
import { FormikSelectInput } from "components/UI/Inputs/FormikInputs";
import { RNHEstablecimientoGet } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoGet";
import { RNHCargoGet } from "services/services-mia/programa-salud/personal/RNHCargoGet";
import { RNHFuncionGet } from "services/services-mia/programa-salud/personal/RNHFuncionGet";
import { RNHAgrupamientoGet } from "services/services-mia/programa-salud/personal/RNHAgrupamientoGet";
import { RNHAgenteAsignarDo } from "services/services-mia/programa-salud/personal/RNHAgenteAsignarDo";
import { RNHHorarioGet } from "services/services-mia/programa-salud/personal/RNHHorarioGet";
import { RNHLugarOrganigramaGet } from "services/services-mia/programa-salud/estructuras/RNHLugarOrganigramaGet";
import {
  TIPOS_DE_COMPONENTE,
  tiposHabilitadosPorComponenteMaximo,
} from "consts/tipos-de-componente";
import styles from "./AsignarModal.module.scss";

/**
 * La etiqueta de un campo, con su aviso al lado.
 *
 * AL LADO Y NO DEBAJO para no gastar el renglón que `FormikInputs` reserva bajo cada
 * campo: son cuatro campos con aviso, y cuatro renglones de más le sacaban scroll
 * vertical al modal.
 *
 * Se puede porque este formulario NO VALIDA —no tiene `validationSchema`, así que no hay
 * error que pueda ocupar ese renglón—, y por eso cada campo lo renuncia con
 * `helperText=""`. En un formulario con validación esto estaría mal: el error aparecería
 * de golpe desplazando todo lo de abajo.
 *
 * MUI escala la etiqueta a 0.75 cuando está `shrink`, así que el aviso termina bastante
 * más chico que el nombre del campo. Es lo que se busca: se lee como una aclaración y no
 * como parte del nombre.
 */
function EtiquetaConAyuda({ texto, ayuda }) {
  if (!ayuda) return texto;
  return (
    <>
      {texto}
      <span className={styles.ayudaEtiqueta}>{ayuda}</span>
    </>
  );
}

function CargarAlCambiar({ onCambio }) {
  const { values } = useFormikContext();
  const { hospitalCod, componenteCod, cargoCod } = values;

  useEffect(() => {
    onCambio({ hospitalCod, componenteCod, cargoCod });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalCod, componenteCod, cargoCod]);

  return null;
}

/**
 * Asignación de un agente a un componente de la estructura.
 *
 * El recorrido es en cascada y cada paso habilita al siguiente:
 *
 *   1. HOSPITAL   — dentro de cuál se va a buscar.
 *   2. CARGO      — dos opciones como mucho: el cargo del agente y "Sin cargo". NO es
 *                   para asignarle un cargo —eso se hace en Datos laborales— sino para
 *                   decir si ESTA asignación es donde lo ejerce.
 *   3. TIPO       — lo FIJA el cargo cuando se eligió el del agente, porque un cargo
 *                   guarda su `rnhTipoComponenteCod`. Con "Sin cargo" queda libre.
 *   4. DESTINO    — los componentes de ese hospital y de ese tipo.
 *
 * El paso 3 no es decorativo: sin un tipo elegido no se pide la lista de componentes,
 * y así se evita traer el árbol entero del hospital para después descartarlo.
 *
 * POR QUÉ EL CARGO ES UN SELECTOR Y NO UN CHECKBOX
 *
 * Un agente ejerce su cargo en UN solo lugar, pero si tiene asignación múltiple puede
 * trabajar en varios. Con el cargo fijo, el tipo de repartición quedaba trabado SIEMPRE
 * y no había forma de mandarlo a ningún otro lado.
 *
 * Elegir "Sin cargo" acá no le saca el cargo al agente: dice que en ESTE lugar no lo
 * ejerce, y con eso el tipo se libera. Por eso también desapareció el checkbox "Ejerce
 * acá su cargo": era la misma pregunta hecha dos veces, y las dos se podían contradecir.
 *
 * "SIN CARGO" DEL CATALOGO ES UN CARGO DE VERDAD, con su cod, pero sin
 * `rnhTipoComponenteCod`: es el unico sin tipo, justamente porque un agente sin cargo
 * puede ir a cualquier nodo. De ahi que el flag que viaja al back se derive de "el
 * valor elegido define un tipo" y no de "hay un valor elegido".
 *
 * EL AGRUPAMIENTO NO VIAJA: lo que se asigna sigue siendo la funcion. Es un paso previo
 * OBLIGATORIO igual: hasta que no se elige uno, el selector de funciones queda apagado.
 * Cargar las dos listas enteras y dejar elegir en cualquiera volvia decorativa la
 * dependencia entre ellas.
 *
 * CONSECUENCIA A TENER PRESENTE: una funcion SIN agrupamiento no se puede elegir desde
 * aca, porque no cuelga de ninguna opcion de la primera lista.
 *
 * Las funciones se PIDEN al elegir el agrupamiento, con `rnhFuncionAgrupamientoId`: no se
 * trae el catalogo entero para recortarlo aca. Ademas de traer menos, evita el match por
 * nombre —el unico vinculo que daba la response— que se rompia al renombrar un
 * agrupamiento.
 *
 * OJO QUE `rnhFuncionAgrupamientoId` NO SIGNIFICA LO MISMO DE IDA QUE DE VUELTA: como
 * param viaja el ID, pero en la response el campo con ese mismo nombre trae el NOMBRE del
 * agrupamiento. Aca eso no molesta porque el recorte lo hace el back; al modal de edicion
 * de Funciones si lo obliga a traducir.
 *
 * EL HORARIO SOLO SE ELIGE SI EL AGENTE ES DE HORARIO FIJO ("F"). Con "R" la jornada la
 * define la planilla de Control de Horarios y no una asignacion suelta, y sin tipo
 * cargado todavia no hay nada que decidir: el camino es ponerselo en Datos laborales.
 * En los dos casos el selector queda apagado y no viaja nada.
 *
 * EL HOSPITAL MANDA SOBRE EL TIPO. Cada nivel de complejidad define un componente
 * maximo, asi que un hospital que arranca en unidad no tiene departamentos. La funcion
 * que lo resuelve falla ABIERTA: un campo mal leido no da error, habilita todo.
 *
 * LOS VALORES IMPUESTOS VAN EN EFECTOS, NO EN `initialValues`. Este modal queda MONTADO
 * dentro del detalle, asi que sus valores iniciales se evaluan una sola vez, cuando
 * `datosAgente` todavia es null. Y ademas pueden cambiar sin cerrar el detalle, si se
 * edita el hospital o el cargo desde Datos laborales.
 */
export default function AsignarModal({ row, datosAgente, updateList, closeModal }) {
  const credentials = useRecoilValue(userCredentials);
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const formikRef = useRef();

  const [hospitales, setHospitales] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [funciones, setFunciones] = useState([]);
  const [cargandoFunciones, setCargandoFunciones] = useState(false);
  const [agrupamientos, setAgrupamientos] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(false);
  const [destinos, setDestinos] = useState([]);
  const [cargandoDestinos, setCargandoDestinos] = useState(false);
  const [inRequest, setInRequest] = useState(false);

  const cargoDelAgente = datosAgente?.rnhCargoCod ?? null;

  const tipoHorarioDelAgente = datosAgente?.rnhAgenteHorarioTipo ?? "";
  const horarioEsFijo = tipoHorarioDelAgente === "F";

  const lugares = Array.isArray(datosAgente?.rnhAgenteLugares)
    ? datosAgente.rnhAgenteLugares
    : [];
  const yaEjerceElCargo = lugares.some((l) => l.rnhAgenteLugarCargo === "Y");

  const esUnico = datosAgente?.rnhAgenteEstablecimientoUnico === "Y";
  const hospitalDelAgente = datosAgente?.rnhEstablecimientoCod ?? "";
  const unicoSinHospital = esUnico && !hospitalDelAgente;

  const yaHayPrincipal =
    yaEjerceElCargo || lugares.some((l) => l.rnhLugarPrincipal === "Y");

  useEffect(() => {
    (async () => {
      setCargandoCatalogos(true);
      // Las funciones NO estan aca: dependen del agrupamiento y se piden al elegirlo.
      const [hosp, carg, agru, hora] = await Promise.all([
        RNHEstablecimientoGet(credentials, { tipo: "H" }),
        RNHCargoGet(credentials),
        RNHAgrupamientoGet(credentials),
        RNHHorarioGet(credentials),
      ]);
      setCargandoCatalogos(false);
      if (hosp.ok) setHospitales(hosp.data);
      // Fila centinela: sin registros el Get devuelve una con todo en null.
      if (agru.ok) setAgrupamientos(agru.data.filter((a) => a?.rnhAgrupamientoId));
      if (hora.ok) setHorarios(hora.data.filter((h) => h?.rnhHorarioId));
      if (!carg.ok) return;
      setCargos(carg.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tipoDeCargo = (cod) =>
    cargos.find((c) => String(c.rnhCargoCod) === String(cod))?.rnhTipoComponenteCod ?? "";

  const tipoDelCargoDelAgente = tipoDeCargo(cargoDelAgente);

  const puedeElegirSuCargo = Boolean(tipoDelCargoDelAgente) && !yaEjerceElCargo;

  const nombreDelCargoDelAgente =
    cargos.find((c) => String(c.rnhCargoCod) === String(cargoDelAgente))?.rnhCargoNombre ??
    "Su cargo";

  useEffect(() => {
    if (!cargos.length) return;
    formikRef.current?.setFieldValue("cargoCod", puedeElegirSuCargo ? cargoDelAgente : "");
    if (puedeElegirSuCargo) {
      formikRef.current?.setFieldValue("componenteCod", tipoDelCargoDelAgente);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargos, cargoDelAgente, puedeElegirSuCargo, tipoDelCargoDelAgente]);

  useEffect(() => {
    if (!esUnico) return;
    formikRef.current?.setFieldValue("hospitalCod", hospitalDelAgente);
    formikRef.current?.setFieldValue("estructuraId", "");
  }, [esUnico, hospitalDelAgente]);

  const cargarFunciones = async (agrupamientoId) => {
    if (!agrupamientoId) {
      setFunciones([]);
      return;
    }
    setCargandoFunciones(true);
    const respuesta = await RNHFuncionGet(credentials, { agrupamientoId });
    setCargandoFunciones(false);
    setFunciones(
      respuesta.ok ? respuesta.data.filter((f) => f?.rnhFuncionCod) : []
    );
  };

  const cargarDestinos = async ({ hospitalCod, componenteCod, cargoCod }) => {
    if (!hospitalCod || !componenteCod) {
      setDestinos([]);
      return;
    }
    setCargandoDestinos(true);
    const respuesta = await RNHLugarOrganigramaGet(credentials, {
      establecimientoCod: hospitalCod,
      cargoCod,
      componenteCod,
    });
    setCargandoDestinos(false);

    if (!respuesta.ok) {
      setDestinos([]);
      return;
    }
    setDestinos(
      respuesta.data.map((d, i) => ({
        estructuraId: d.rnhEstablecimientoEstructuraId ?? i,
        nombre: d.rnhEstablecimientoEstructuraNombre ?? "",
        cod: d.rnhEstablecimientoEstructuraCod ?? "",
      }))
    );
  };

  const asignar = async (values) => {
    setInRequest(true);

    const lugarCargo = tipoDeCargo(values.cargoCod) ? "Y" : "N";

    const respuesta = await RNHAgenteAsignarDo(credentials, {
      agenteId: row?.id,
      establecimientoEstructuraId: values.estructuraId,
      funcionCod: values.funcionCod,
      // Explicito y no apoyado en que el select este deshabilitado: este modal queda
      // MONTADO dentro del detalle, asi que el tipo puede cambiar desde Datos laborales
      // con un valor ya elegido en el formulario.
      horarioId: horarioEsFijo ? values.horarioId : null,
      lugarCargo,
      lugarPrincipal: lugarCargo === "Y" ? "Y" : values.lugarPrincipal,
    });
    setInRequest(false);

    if (!respuesta.ok) return;

    setSnackBarInfo({
      message: "Agente asignado correctamente",
      severity: "success",
      open: true,
    });
    updateList?.();
    closeModal?.();
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        hospitalCod: "",
        cargoCod: "",
        componenteCod: "",
        estructuraId: "",
        agrupamientoId: "",
        funcionCod: "",
        horarioId: "",
        lugarPrincipal: "N",
      }}
      onSubmit={asignar}
    >
      {({ values, setFieldValue }) => {
        const ejerceElCargo = Boolean(tipoDeCargo(values.cargoCod));

        const hospitalElegido = hospitales.find(
          (h) => String(h.rnhEstablecimientoCod) === String(values.hospitalCod)
        );
        const codsHabilitados = tiposHabilitadosPorComponenteMaximo(
          hospitalElegido?.rnhEstablecimientoNivelComponenteMaximoCod
        );
        const tiposDelHospital = TIPOS_DE_COMPONENTE.filter(({ cod }) =>
          codsHabilitados.includes(cod)
        );

        const cargoNoEntraEnElHospital =
          ejerceElCargo &&
          Boolean(values.hospitalCod) &&
          !codsHabilitados.includes(tipoDeCargo(values.cargoCod));

        const ayudaHospital = unicoSinHospital
          ? "Cargale un hospital en Datos laborales"
          : esUnico
            ? "Único: solo dentro de su hospital"
            : "";

        const ayudaCargo = yaEjerceElCargo
          ? "Ya ejerce su cargo en otro lugar"
          : cargos.length > 0 && !puedeElegirSuCargo
            ? "Sin cargo: va a cualquier repartición"
            : "";

        // Los avisos de estos dos campos van ABAJO y a media columna: entran unos 30
        // caracteres por renglon. Mas largos envuelven, estiran la fila y le dejan un
        // hueco al campo de al lado, que casi nunca tiene aviso al mismo tiempo.
        const ayudaTipo = cargoNoEntraEnElHospital
          ? "El hospital no llega ahí"
          : "";

        const ayudaHorario = horarioEsFijo
          ? ""
          : tipoHorarioDelAgente === "R"
            ? "Rotativo: no se fija acá"
            : "Falta su tipo de horario";

        const ayudaPrincipal = ejerceElCargo
          ? "Ejerce acá: es su lugar principal"
          : yaHayPrincipal
            ? "Ya tiene otro lugar principal"
            : "";

        return (
          <Form className={styles.contenedor} noValidate>
            <CargarAlCambiar onCambio={cargarDestinos} />
            {/* DOS COLUMNAS: a la izquierda lo que se carga, a la derecha el catálogo
                de reparticiones que esos campos filtran. Una debajo de la otra el modal
                crecía a lo alto hasta sacar scroll; al costado, el alto lo define la
                columna más larga y sobra ancho para los avisos. */}
            <div className={styles.columnas}>
              <div className={styles.campos}>
                <FormikSelectInput
                  fullWidth
                  name="hospitalCod"
                  labelText={<EtiquetaConAyuda texto="Hospital" ayuda={ayudaHospital} />}
                  helperText=""
                  disabled={cargandoCatalogos || esUnico}
                  onChange={(e) => {
                    const cod = e.target.value;
                    setFieldValue("hospitalCod", cod);
                    setFieldValue("estructuraId", "");

                    if (tipoDeCargo(values.cargoCod)) return;
                    const hosp = hospitales.find(
                      (h) => String(h.rnhEstablecimientoCod) === String(cod)
                    );
                    const habilitados = tiposHabilitadosPorComponenteMaximo(
                      hosp?.rnhEstablecimientoNivelComponenteMaximoCod
                    );
                    if (values.componenteCod && !habilitados.includes(values.componenteCod)) {
                      setFieldValue("componenteCod", "");
                    }
                  }}
                >
                  <option value="" disabled>
                    Seleccione
                  </option>
                  {hospitales.map((h) => (
                    <option key={h.rnhEstablecimientoCod} value={h.rnhEstablecimientoCod}>
                      {h.rnhEstablecimientoNombre}
                    </option>
                  ))}
                </FormikSelectInput>

                <FormikSelectInput
                  fullWidth
                  name="cargoCod"
                  labelText={<EtiquetaConAyuda texto="Cargo" ayuda={ayudaCargo} />}
                  helperText=""
                  disabled={cargandoCatalogos || !puedeElegirSuCargo}
                  onChange={(e) => {
                    const cod = e.target.value;
                    setFieldValue("cargoCod", cod);
                    setFieldValue("estructuraId", "");
                    const tipo = tipoDeCargo(cod) || values.componenteCod;
                    setFieldValue("componenteCod", tipo);
                  }}
                >
                  <option value="">Sin cargo</option>
                  {puedeElegirSuCargo && (
                    <option value={cargoDelAgente}>{nombreDelCargoDelAgente}</option>
                  )}
                </FormikSelectInput>

                <FormikSelectInput
                  fullWidth
                  name="agrupamientoId"
                  labelText="Agrupamiento"
                  helperText=""
                  disabled={cargandoCatalogos}
                  onChange={(e) => {
                    const id = e.target.value;
                    setFieldValue("agrupamientoId", id);
                    // La lista de abajo se reemplaza entera por la del agrupamiento nuevo,
                    // así que lo que estuviera elegido deja de estar a la vista.
                    setFieldValue("funcionCod", "");
                    cargarFunciones(id);
                  }}
                >
                  <option value="" disabled>
                    Seleccione
                  </option>
                  {agrupamientos.map((a) => (
                    <option key={a.rnhAgrupamientoId} value={a.rnhAgrupamientoId}>
                      {a.rnhAgrupamientoNombre}
                    </option>
                  ))}
                </FormikSelectInput>

                <FormikSelectInput
                  fullWidth
                  name="funcionCod"
                  labelText="Función"
                  helperText=""
                  disabled={cargandoFunciones || funciones.length === 0}
                >
                  {/* El aviso va en la opción vacía: es el mismo recurso que usa "Elegí un
                      hospital" en Tipo de repartición, y no gasta ni ancho ni alto. */}
                  <option value="" disabled>
                    {!values.agrupamientoId
                      ? "Elegí un agrupamiento"
                      : cargandoFunciones
                        ? "Cargando…"
                        : funciones.length
                          ? "Seleccione"
                          : "Ese agrupamiento no tiene funciones"}
                  </option>
                  {funciones.map((f) => (
                    <option key={f.rnhFuncionCod} value={f.rnhFuncionCod}>
                      {f.rnhFuncionNombre}
                    </option>
                  ))}
                </FormikSelectInput>

                {/* Estos dos comparten renglón: son los de etiqueta más corta que quedan.
                    Sus avisos vuelven ABAJO del campo en vez de ir al lado de la etiqueta,
                    porque a media columna MUI corta la etiqueta con puntos suspensivos
                    mientras que el texto de abajo envuelve y se sigue leyendo entero.

                    El `|| " "` reserva ese renglón aunque no haya aviso: sin él, la fila
                    cambia de alto al aparecer uno y empuja lo que viene abajo. */}
                <div className={styles.fila}>
                  <FormikSelectInput
                    fullWidth
                    name="componenteCod"
                    labelText="Tipo de repartición"
                    helperText={ayudaTipo || " "}
                    disabled={ejerceElCargo || !values.hospitalCod}
                  >
                    <option value="" disabled>
                      {values.hospitalCod ? "Seleccione" : "Elegí un hospital"}
                    </option>
                    {tiposDelHospital.map(({ cod, label }) => (
                      <option key={cod} value={cod}>
                        {label}
                      </option>
                    ))}
                  </FormikSelectInput>

                  <FormikSelectInput
                    fullWidth
                    name="horarioId"
                    labelText="Horario"
                    helperText={ayudaHorario || " "}
                    disabled={cargandoCatalogos || !horarioEsFijo}
                  >
                    <option value="">Sin asignar</option>
                    {horarios.map((h) => (
                      <option key={h.rnhHorarioId} value={h.rnhHorarioId}>
                        {h.rnhHorarioNombre}
                      </option>
                    ))}
                  </FormikSelectInput>
                </div>

                {/* Al pie de la pila: es la única decisión que no alimenta la búsqueda de
                    reparticiones, sino que califica a la asignación que sale de ella. */}
                <div className={styles.marcas}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={ejerceElCargo || values.lugarPrincipal === "Y"}
                        disabled={ejerceElCargo || yaHayPrincipal}
                        onChange={(e) =>
                          setFieldValue("lugarPrincipal", e.target.checked ? "Y" : "N")
                        }
                      />
                    }
                    label="Lugar de asignación principal"
                  />
                  {ayudaPrincipal && (
                    <span className={styles.ayuda}>{ayudaPrincipal}</span>
                  )}
                </div>
              </div>

              <div className={styles.destinos}>
                <header className={styles.destinosHeader}>
                  <span>Reparticiones disponibles</span>
                </header>
                <div className={styles.destinosBody}>
                  {cargandoDestinos ? (
                    <CircularIndeterminate />
                  ) : !values.hospitalCod || !values.componenteCod ? (
                    <span className={styles.vacio}>
                      Elegí un hospital y un tipo de repartición para ver las opciones.
                    </span>
                  ) : destinos.length === 0 ? (
                    <span className={styles.vacio}>
                      Este hospital no tiene reparticiones de ese tipo.
                    </span>
                  ) : (
                    destinos.map((d) => (
                      <button
                        key={d.estructuraId}
                        type="button"
                        className={[
                          styles.destino,
                          String(values.estructuraId) === String(d.estructuraId) &&
                            styles.destinoElegido,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => setFieldValue("estructuraId", d.estructuraId)}
                      >
                        <Radio
                          size="small"
                          checked={String(values.estructuraId) === String(d.estructuraId)}
                          readOnly
                        />
                        <span className={styles.destinoDatos}>
                          <span className={styles.destinoNombre}>{d.nombre}</span>
                          {d.cod && <span className={styles.destinoCod}>{d.cod}</span>}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            <FormButton
              inRequest={inRequest}
              submitDisabled={
                !values.estructuraId || !values.funcionCod || unicoSinHospital
              }
              newFormData={{ button: "Asignar" }}
              onCloseModal={closeModal}
            />
            {inRequest && <SpinnerOverlay />}
          </Form>
        );
      }}
    </Formik>
  );
}
